import React from 'react';
import ChangeToneIcon from '@atlaskit/icon/core/microphone';
import { fg } from '@atlaskit/platform-feature-flags';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { CONFIG_ITEM_KEYS } from '../../../config-items/config-item-keys';
import { createEditorPluginAIConfigItemMarkdown } from '../../../config-items/config-items';
import { sliceOrNodeToMarkdown } from '../../../config-items/slice-or-node-to-markdown';
import { streamConvoAI } from '../../../provider/prompt-requests/convo-ai';
import { streamXPGenAI } from '../../../provider/prompt-requests/xp-gen-ai';
import { Icon as CasualIcon } from '../../assets/icons/toneCasual';
import { Icon as EducationalIcon } from '../../assets/icons/toneEducational';
import { Icon as EmpatheticIcon } from '../../assets/icons/toneEmpathetic';
import { Icon as NeutralIcon } from '../../assets/icons/toneNeutral';
import { Icon as ProfessionalIcon } from '../../assets/icons/toneProfessional';
import { createInsertBelow, createReplace } from '../../config-item-actions/markdown';
import { isMinLength } from '../../config-item-visible/visibility-utils';
import { getSlice } from '../utils';
import { messages } from './messages';
var SELECTION_TYPE = 'range';
function createChangeToneOption(_ref) {
  var key = _ref.key,
    title = _ref.title,
    description = _ref.description,
    intentSchemaId = _ref.intentSchemaId,
    icon = _ref.icon,
    shortTitle = _ref.shortTitle;
  return createEditorPluginAIConfigItemMarkdown({
    key: key,
    title: title,
    description: description,
    selectionType: SELECTION_TYPE,
    nestingConfig: {
      parentTestId: 'change-tone-to',
      parentTitle: editorExperiment('platform_editor_ai_command_palette_post_ga', 'test') ? messages.nestingParentTitlePostGA : messages.nestingParentTitle,
      shortTitle: shortTitle,
      icon: function icon() {
        return /*#__PURE__*/React.createElement(ChangeToneIcon, {
          label: "",
          spacing: "spacious",
          color: "var(--ds-icon, #44546F)"
        });
      }
    },
    getInitialContext: function getInitialContext(_ref2) {
      var editorView = _ref2.editorView,
        positions = _ref2.positions,
        intl = _ref2.intl,
        updateIdMap = _ref2.updateIdMap,
        mentionMap = _ref2.mentionMap;
      var _sliceOrNodeToMarkdow = sliceOrNodeToMarkdown({
          slice: getSlice(editorView, positions),
          editorView: editorView,
          convertTo: 'markdown-plus',
          updateIdMap: updateIdMap,
          selectionType: SELECTION_TYPE,
          mentionMap: mentionMap
        }),
        markdown = _sliceOrNodeToMarkdow.markdown,
        contentStatistics = _sliceOrNodeToMarkdow.contentStatistics;
      return {
        selection: markdown,
        userLocale: intl.locale,
        intentSchemaId: intentSchemaId,
        contentStatistics: contentStatistics
      };
    },
    triggerPromptRequest: function triggerPromptRequest(_ref3) {
      var initialContext = _ref3.initialContext,
        editorSchema = _ref3.editorSchema,
        analyticsContext = _ref3.analyticsContext;
      if (fg('platform_editor_ai_assistance_service')) {
        return streamConvoAI({
          aiSessionId: analyticsContext === null || analyticsContext === void 0 ? void 0 : analyticsContext.aiSessionId,
          userLocale: initialContext.userLocale,
          intentSchemaId: intentSchemaId,
          editorSchema: editorSchema,
          fullDocument: initialContext.document,
          currentSelection: initialContext.selection
        });
      }
      return streamXPGenAI({
        userLocale: initialContext.userLocale,
        intentSchemaId: intentSchemaId,
        editorSchema: editorSchema,
        contextList: [{
          type: 'ADF_MARKDOWN_V1',
          entity: initialContext.selection,
          relationship: 'SELECTION'
        }]
      });
    },
    isVisible: function isVisible(_ref4) {
      var editorView = _ref4.editorView,
        positions = _ref4.positions;
      return isMinLength({
        editorView: editorView,
        minLength: 1,
        positions: positions
      });
    },
    icon: icon,
    actions: [createInsertBelow({
      appearance: 'secondary'
    }), createReplace({
      appearance: 'primary'
    })],
    getBackendModel: function getBackendModel() {
      if (fg('platform_editor_ai_assistance_service')) {
        return 'assistance-service';
      }
      return 'xp-gen-ai';
    }
  });
}
export var neutralTone = createChangeToneOption({
  key: CONFIG_ITEM_KEYS.CHANGE_TONE_TO_NEUTRAL,
  title: messages.neutralToneTitle,
  shortTitle: messages.neutralToneShortTitle,
  description: messages.neutralToneDescription,
  intentSchemaId: 'change_tone_neutral_intent_schema.json',
  icon: function icon(_ref5) {
    var shownAt = _ref5.shownAt;
    return /*#__PURE__*/React.createElement(NeutralIcon, {
      shownAt: shownAt
    });
  }
});
export var professionalTone = createChangeToneOption({
  key: CONFIG_ITEM_KEYS.CHANGE_TONE_TO_PROFESSIONAL,
  title: messages.professionalToneTitle,
  shortTitle: messages.professionalToneShortTitle,
  description: messages.professionalToneDescription,
  intentSchemaId: 'change_tone_professional_intent_schema.json',
  icon: function icon(_ref6) {
    var shownAt = _ref6.shownAt;
    return /*#__PURE__*/React.createElement(ProfessionalIcon, {
      shownAt: shownAt
    });
  }
});
export var casualTone = createChangeToneOption({
  key: CONFIG_ITEM_KEYS.CHANGE_TONE_TO_CASUAL,
  title: messages.casualToneTitle,
  shortTitle: messages.casualToneShortTitle,
  description: messages.casualToneDescription,
  intentSchemaId: 'change_tone_business_casual_intent_schema.json',
  icon: function icon(_ref7) {
    var shownAt = _ref7.shownAt;
    return /*#__PURE__*/React.createElement(CasualIcon, {
      shownAt: shownAt
    });
  }
});
export var educationalTone = createChangeToneOption({
  key: CONFIG_ITEM_KEYS.CHANGE_TONE_TO_EDUCATIONAL,
  title: messages.educationalToneTitle,
  shortTitle: messages.educationalToneShortTitle,
  description: messages.educationalToneDescription,
  intentSchemaId: 'change_tone_educational_intent_schema.json',
  icon: function icon(_ref8) {
    var shownAt = _ref8.shownAt;
    return /*#__PURE__*/React.createElement(EducationalIcon, {
      shownAt: shownAt
    });
  }
});
export var empatheticTone = createChangeToneOption({
  key: CONFIG_ITEM_KEYS.CHANGE_TONE_TO_EMPATHETIC,
  title: messages.empatheticToneTitle,
  shortTitle: messages.empatheticToneShortTitle,
  description: messages.empatheticToneDescription,
  intentSchemaId: 'change_tone_empathetic_intent_schema.json',
  icon: function icon(_ref9) {
    var shownAt = _ref9.shownAt;
    return /*#__PURE__*/React.createElement(EmpatheticIcon, {
      shownAt: shownAt
    });
  }
});